import { Head } from 'vite-react-ssg';
import { ArrowRight, Bot, Brain, Cpu, DollarSign, Calendar, Shield, Zap, BookOpen, BarChart3, Globe, HardDrive } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";
import ContentLayout from "@/components/ContentLayout";
import heroImage from "@/assets/deepseek-v4-hero.jpg";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";
import { COMPETITOR_PRICING, multipleVs } from "@/lib/competitor-pricing";

const DeepseekV4 = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const flash = DEEPSEEK_PRICING.models["v4-flash"];
  const pro = DEEPSEEK_PRICING.models["v4-pro"];
  const gpt54 = COMPETITOR_PRICING.models["gpt-5.4"];
  const opus48 = COMPETITOR_PRICING.models["claude-opus-4.8"];


  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "DeepSeek V4: Everything You Need to Know About V4-Pro (1.6T MoE) and V4-Flash (284B MoE)",
    "datePublished": "2026-04-04T08:00:00+00:00",
    "dateModified": "2026-06-28T08:00:00+00:00",
    "author": { "@type": "Organization", "name": "The Independent DeepSeek Guide", "url": "https://deepseek.ai" },
    "publisher": { "@type": "Organization", "name": "The Independent DeepSeek Guide", "logo": { "@type": "ImageObject", "url": "https://deepseek.ai/logo.png" } },
    "description": "DeepSeek V4 complete 2026 guide: V4-Pro (1.6T total / 49B active MoE) and V4-Flash (284B total / 13B active MoE), benchmarks vs GPT-5.4 and Claude Opus 4.8, API pricing from $0.14/M tokens, 1M-token context and release timeline.",
    "mainEntityOfPage": { "@type": "WebPage", "@id": "https://deepseek.ai/deepseek-v4" },
    "image": "https://deepseek.ai/deepseek-v4-hero.jpg"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is DeepSeek V4 better than ChatGPT (GPT-5.4)?",
        "acceptedAnswer": { "@type": "Answer", "text": "Based on leaked benchmarks, DeepSeek V4 rivals or slightly beats GPT-5.4 and Claude 4.5 in complex software engineering tasks (SWE-bench) and repository-level coding, especially given its 1M token context. However, independent testing is required to declare an absolute winner once the model officially launches." }
      },
      {
        "@type": "Question",
        "name": "Can I run DeepSeek V4 locally?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes, assuming the planned open-weight release happens. Because the Mixture-of-Experts design only activates ~32B parameters at a time, a quantized version (INT4) can theoretically run locally on a single 32GB RTX 5090, or dual RTX 4090s." }
      },
      {
        "@type": "Question",
        "name": "Why is DeepSeek V4 so cheap?",
        "acceptedAnswer": { "@type": "Answer", "text": "The low costs are driven by the highly efficient MoE architecture (activating only a fraction of the model), low training costs (estimated at ~$10M compared to >$100M for Western models), and the implementation of DeepSeek Sparse Attention, which halves the compute needed for long contexts." }
      },
      {
        "@type": "Question",
        "name": "Is DeepSeek V4 multimodal?",
        "acceptedAnswer": { "@type": "Answer", "text": "No. Both shipped variants, V4-Pro and V4-Flash, are text-only mixture-of-experts language models. The model cards describe no vision, audio or video modality. Pre-launch reports of native multimodal training did not ship." }

      }
    ]
  };

  return (
    <>
      <Head>
        <title>DeepSeek V4 Explained: V4-Pro 1.6T vs V4-Flash 284B (2026)</title>
        <meta name="description" content="What DeepSeek V4 actually is: 1.6T and 284B MoE models, 1M context, 384K output, real benchmarks and prices. Independent, sourced, updated 25 July." />
        <link rel="canonical" href="https://deepseek.ai/deepseek-v4" />
        <meta property="og:title" content="DeepSeek V4: Everything You Need to Know About V4-Pro and V4-Flash" />
        <meta property="og:description" content="DeepSeek V4-Pro (1.6T MoE) and V4-Flash (284B MoE), 1M-token context, benchmarks and disruptive API pricing. The complete guide." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://deepseek.ai/deepseek-v4" />
        <meta property="og:image" content="https://deepseek.ai/deepseek-v4-hero.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek V4: V4-Pro (1.6T) and V4-Flash (284B) MoE — Complete Guide" />
        <meta name="twitter:description" content="Everything about DeepSeek V4-Pro and V4-Flash MoE: architecture, benchmarks, pricing, and release date." />
        <meta name="keywords" content="DeepSeek V4, DeepSeek V4 Pro, DeepSeek V4 Flash, DeepSeek V4 release date, DeepSeek V4 benchmarks, DeepSeek V4 API pricing, 1.6T MoE, Engram memory, DeepSeek vs GPT-5, DeepSeek vs Claude 4.5" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <NavigationButtons />
            <div className="flex items-center gap-2 mb-4 mt-4 flex-wrap">
              <Badge variant="outline" className="text-primary border-primary/30">Focus Keyword: DeepSeek V4</Badge>
              <Badge variant="secondary">Updated June 28, 2026</Badge>
              <Badge variant="secondary">16 min read</Badge>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6 leading-tight">
              DeepSeek V4: Everything You Need to Know About V4-Pro (1.6T MoE) and V4-Flash (284B MoE)
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl">
              The complete 2026 guide to DeepSeek V4 — the mixture-of-experts family that launched in April 2026 and reshaped the AI pricing landscape. V4-Pro totals <strong>1.6 trillion parameters</strong> with ~49 billion active per token; V4-Flash totals <strong>284 billion parameters</strong> with ~13 billion active. Both models share a 1M-token context window. This page covers the architecture, real benchmarks against GPT-5.4 and Claude Opus 4.8, and current API pricing.
            </p>
            <img
              src={heroImage}
              alt="DeepSeek V4 neural network visualization — 1.6T-parameter MoE architecture"
              width={1200}
              height={630}
              className="w-full rounded-xl shadow-lg border border-border"
            />
          </div>
        </div>

        <ContentLayout>
          {/* Table of Contents */}
          <Card className="mb-10 border-primary/20">
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" /> Table of Contents
              </h2>
              <nav>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li><a href="#v4-family-2026" className="text-primary hover:underline">The DeepSeek V4 Family in 2026 (Pro, Flash & Base)</a></li>
                  <li><a href="#what-is-deepseek-v4" className="text-primary hover:underline">What is DeepSeek V4? (Core Specs)</a></li>
                  <li><a href="#architectural-innovations" className="text-primary hover:underline">3 Groundbreaking Architectural Innovations</a></li>
                  <li><a href="#benchmarks" className="text-primary hover:underline">Benchmarks: DeepSeek V4 vs. GPT-5.4 and Claude 4.5</a></li>
                  <li><a href="#api-pricing" className="text-primary hover:underline">API Pricing: The Most Cost-Effective Frontier AI</a></li>
                  <li><a href="#hardware" className="text-primary hover:underline">Geopolitics and Hardware: The Shift to Huawei</a></li>
                  <li><a href="#release-date" className="text-primary hover:underline">Release Date: When is DeepSeek V4 Coming Out?</a></li>
                  <li><a href="#faq" className="text-primary hover:underline">FAQ</a></li>
                </ol>
              </nav>
            </CardContent>
          </Card>

          {/* Introduction */}
          <section className="prose prose-lg max-w-none mb-12">
            <p className="text-foreground leading-relaxed">
              The world of artificial intelligence is on the verge of a massive shift. <strong>DeepSeek V4</strong> is the flagship model family from DeepSeek that pushes the boundaries of parameter scale while promising unprecedented efficiency. V4-Pro totals <strong>1.6 trillion parameters</strong> (~49B active per token via MoE), while V4-Flash totals <strong>284 billion parameters</strong> (~13B active). Both are <strong>text-only</strong> and offer a <strong>1 million token</strong> context window, positioning them as direct competitors to Western giants like OpenAI's GPT-5.4 and Anthropic's Claude Opus 4.8.
            </p>
            <p className="text-foreground leading-relaxed">
              In this comprehensive article, we dive into the key specifications, architectural innovations, expected pricing, and the strategic hardware shifts behind DeepSeek V4.
            </p>
          </section>

          {/* Key Takeaways */}
          <Card className="mb-12 bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-primary">
                <Zap className="h-5 w-5" /> Key Takeaways
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2"><span className="text-primary font-bold">•</span><span><strong>V4-Pro 1.6T / V4-Flash 284B parameters</strong> with ~49B / ~13B active per token via MoE</span></li>
                <li className="flex items-start gap-2"><span className="text-primary font-bold">•</span><span><strong>1 Million token context window</strong> — equivalent to 15–20 full novels</span></li>
                <li className="flex items-start gap-2"><span className="text-primary font-bold">•</span><span><strong>Text-only:</strong> no vision, audio or video modality in either shipped variant</span></li>
                <li className="flex items-start gap-2"><span className="text-primary font-bold">•</span><span><strong>10–50x cheaper</strong> API pricing than GPT-5.4 and Claude Opus 4.8</span></li>
                <li className="flex items-start gap-2"><span className="text-primary font-bold">•</span><span><strong>Open-source</strong> weights released under the MIT license</span></li>
                <li className="flex items-start gap-2"><span className="text-primary font-bold">•</span><span><strong>Runnable locally</strong> on dual RTX 4090s or single RTX 5090</span></li>
              </ul>
            </CardContent>
          </Card>

          <section id="v4-family-2026" className="mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 flex items-center gap-3">
              <Bot className="h-7 w-7 text-primary" /> The DeepSeek V4 Family in 2026
            </h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              DeepSeek V4 launched on 24 April 2026 as a two-model family. There is no separate "base" model alongside them — the official release and API docs list exactly two variants: <strong>V4 Pro</strong> (1.6T total / 49B active) and <strong>V4 Flash</strong> (284B total / 13B active). Both share the same architecture, 1M-token context, and MIT-licensed open weights.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Card className="border-primary/30">
                <CardContent className="pt-6">
                  <Badge className="mb-2 bg-primary text-primary-foreground">Flagship · Best value</Badge>
                  <h3 className="font-semibold text-foreground mb-2">DeepSeek V4 Pro</h3>
                  <p className="text-sm text-muted-foreground mb-3">1.6T total / 49B active MoE. Production-tuned tier. As of 31 May 2026, the previous 75% price cut became <strong>permanent</strong>: $0.435/M input (cache miss), $0.87/M output.</p>
                  <a href="/blog/deepseek-v4-pro-api-price-cut-permanent" className="text-sm text-primary hover:underline">Read the price-cut analysis →</a>
                </CardContent>
              </Card>
              <Card className="border-primary/30">
                <CardContent className="pt-6">
                  <Badge className="mb-2 bg-primary text-primary-foreground">Fastest · Cheapest</Badge>
                  <h3 className="font-semibold text-foreground mb-2">DeepSeek V4 Flash</h3>
                  <p className="text-sm text-muted-foreground mb-3">284B total / 13B active MoE. Latency-optimised variant with the same 1M context. $0.14/M input (cache miss), $0.28/M output.</p>
                  <a href="/deepseek-v4-flash-review" className="text-sm text-primary hover:underline">V4 Flash deep dive →</a>
                </CardContent>
              </Card>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Both variants share the same V4 architecture covered below — MoE with 13B (Flash) or 49B (Pro) active params per token, text-only inputs, and DeepSeek Sparse Attention. The differences are model size, latency, and pricing.
            </p>
          </section>


          {/* What is DeepSeek V4 */}
          <section id="what-is-deepseek-v4" className="mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Brain className="h-7 w-7 text-primary" /> What is DeepSeek V4? (Core Specs)
            </h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              DeepSeek V4 builds upon the success of its predecessors (like <a href="/blog/deepseek-v31-silent-revolution" className="text-primary hover:underline">V3</a> and <a href="/deepseek-r1" className="text-primary hover:underline">R1</a>) by combining massive scalability with extreme operational cost efficiency. Here are the primary technical specifications:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: <Cpu className="h-5 w-5 text-primary" />, title: "Parameters", desc: "V4-Pro 1.6T total / 49B active · V4-Flash 284B / 13B (MoE)" },
                { icon: <Zap className="h-5 w-5 text-primary" />, title: "Active Parameters", desc: "13B (Flash) or 49B (Pro) activated per token via efficient Mixture-of-Experts (MoE) routing" },
                { icon: <BookOpen className="h-5 w-5 text-primary" />, title: "Context Window", desc: "1 Million tokens — roughly an entire medium-sized codebase or 15–20 full-length novels" },
                { icon: <Globe className="h-5 w-5 text-primary" />, title: "Text-only", desc: "Both V4 variants accept text input only — no vision, audio or video modality" },
              ].map((spec) => (
                <Card key={spec.title} className="border-border">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-2">{spec.icon}<h3 className="font-semibold text-foreground">{spec.title}</h3></div>
                    <p className="text-sm text-muted-foreground">{spec.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Architectural Innovations */}
          <section id="architectural-innovations" className="mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Shield className="h-7 w-7 text-primary" /> Architecture: what V4 actually changes
            </h2>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              V4's efficiency comes from two things DeepSeek documents in the model cards — a stability framework for training a 1.6T-parameter MoE model, and a sparse attention stack for long context. A third line of work, Engram, is frequently attached to V4 online; it is a separate research paper, not a shipped V4 component.
            </p>

            {/* mHC */}
            <Card className="mb-6 border-l-4 border-l-primary">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-foreground mb-3">1. Manifold-Constrained Hyper-Connections (mHC)</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  As AI models scale up, they often suffer from training instability (such as gradient explosion). <strong><a href="/blog/deepseek-mhc-manifold-constrained-hyper-connections" className="text-primary hover:underline">mHC</a></strong> is a mathematical framework that constrains signal amplification, keeping it under 2x (compared to an unconstrained 3000x).
                </p>
                <div className="bg-accent/50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-foreground">⚡ This allows DeepSeek to stably train a 1.6T-parameter MoE model with only a <strong>6.7% computational overhead</strong>.</p>
                </div>
              </CardContent>
            </Card>

            {/* DSA */}
            <Card className="mb-6 border-l-4 border-l-primary">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-foreground mb-3">2. DeepSeek Sparse Attention (DSA)</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  To process 1 million tokens affordably, V4 replaces dense attention with <strong>DeepSeek Sparse Attention</strong>. The V4 model cards describe it in terms of <strong>compressed sparse attention (CSA)</strong> and <strong>hierarchical/coarse-grained attention (HCA)</strong>: the context is compressed into coarse summaries, the relevant regions are selected, and full attention is applied only there. The "Lightning Indexer" often quoted alongside DSA belongs to <strong>V3.2-Exp</strong>, not V4.
                </p>
                <div className="bg-accent/50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-foreground">🚀 The result is roughly linear rather than quadratic cost growth in long-context work, which is what makes 1M-token pricing viable.</p>
                </div>
              </CardContent>
            </Card>

            {/* Engram — adjacent research, not a V4 feature */}
            <Card className="mb-6 border-l-4 border-l-muted">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-foreground mb-3">Related research: Engram conditional memory (not a V4 feature)</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  <strong><a href="/blog/deepseek-engram-v4-architecture" className="text-primary hover:underline">Engram</a></strong> is a DeepSeek research paper on decoupling static factual recall from dynamic reasoning (<a href="https://arxiv.org/abs/2601.07372" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">arXiv:2601.07372</a>). It is <strong>not</strong> part of the shipped V4-Pro or V4-Flash architecture, and DeepSeek has not stated that it will be. Treat reported Engram retrieval gains as results from that paper, not as V4 specifications.
                </p>
              </CardContent>
            </Card>

          </section>

          {/* Benchmarks */}
          <section id="benchmarks" className="mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-3">
              <BarChart3 className="h-7 w-7 text-primary" /> Benchmarks: DeepSeek V4 vs. GPT-5.4 and Claude 4.5
            </h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              DeepSeek V4 is heavily focused on software engineering and deep reasoning. According to leaked internal benchmarks, the model performs at an extraordinary level:
            </p>
            <div className="overflow-x-auto mb-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benchmark</TableHead>
                    <TableHead className="text-center">DeepSeek V4</TableHead>
                    <TableHead className="text-center">GPT-5.4</TableHead>
                    <TableHead className="text-center">Claude Opus 4.8</TableHead>
                    <TableHead className="text-center">DeepSeek V3</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">SWE-bench Verified</TableCell>
                    <TableCell className="text-center font-bold text-primary">&gt;80%</TableCell>
                    <TableCell className="text-center">~80%</TableCell>
                    <TableCell className="text-center">88.6%</TableCell>
                    <TableCell className="text-center text-muted-foreground">~49%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Context Window</TableCell>
                    <TableCell className="text-center font-bold text-primary">1M tokens</TableCell>
                    <TableCell className="text-center">~1M</TableCell>
                    <TableCell className="text-center">1M</TableCell>
                    <TableCell className="text-center text-muted-foreground">128K</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell className="font-medium">Parameters (Total / Active)</TableCell>
                    <TableCell className="text-center font-bold text-primary">1.6T / 49B (Pro)</TableCell>
                    <TableCell className="text-center">Undisclosed</TableCell>
                    <TableCell className="text-center">Undisclosed</TableCell>
                    <TableCell className="text-center text-muted-foreground">671B / 37B</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <Card className="bg-accent/30 border-accent">
              <CardContent className="pt-4 pb-4">
                <p className="text-sm text-muted-foreground">
                  <strong>⚠️ Note:</strong> These impressive numbers are currently based on leaked internal data and are awaiting independent third-party verification upon release.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* API Pricing */}
          <section id="api-pricing" className="mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-3">
              <DollarSign className="h-7 w-7 text-primary" /> API Pricing: The Most Cost-Effective Frontier AI
            </h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Western frontier models are powerful but expensive. GPT-5.4 costs ${gpt54.input.toFixed(2)}/M input (${gpt54.cachedInput?.toFixed(2)}/M cached) and ${gpt54.output.toFixed(2)}/M output under 270K context; Claude Opus 4.8 costs ${opus48.input.toFixed(2)}/M input and ${opus48.output.toFixed(2)}/M output. DeepSeek V4's published rates sit an order of magnitude below both:
            </p>
            <div className="overflow-x-auto mb-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pricing Tier (per 1M tokens)</TableHead>
                    <TableHead className="text-center">DeepSeek V4-Flash</TableHead>
                    <TableHead className="text-center">DeepSeek V4-Pro</TableHead>
                    <TableHead className="text-center">GPT-5.4</TableHead>
                    <TableHead className="text-center">Claude Opus 4.8</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Input (cache miss)</TableCell>
                    <TableCell className="text-center font-bold text-primary">${flash.inputCacheMiss.toFixed(2)}</TableCell>
                    <TableCell className="text-center font-bold text-primary">${pro.inputCacheMiss.toFixed(3)}</TableCell>
                    <TableCell className="text-center">${gpt54.input.toFixed(2)}</TableCell>
                    <TableCell className="text-center">${opus48.input.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Input (cache hit)</TableCell>
                    <TableCell className="text-center font-bold text-primary">${flash.inputCacheHit.toFixed(4)}</TableCell>
                    <TableCell className="text-center font-bold text-primary">${pro.inputCacheHit.toFixed(6)}</TableCell>
                    <TableCell className="text-center">${gpt54.cachedInput?.toFixed(2)}</TableCell>
                    <TableCell className="text-center">${opus48.cachedInput?.toFixed(2)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Output</TableCell>
                    <TableCell className="text-center font-bold text-primary">${flash.output.toFixed(2)}</TableCell>
                    <TableCell className="text-center font-bold text-primary">${pro.output.toFixed(2)}</TableCell>
                    <TableCell className="text-center">${gpt54.output.toFixed(2)}</TableCell>
                    <TableCell className="text-center">${opus48.output.toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mb-6">
              DeepSeek rates verified {DEEPSEEK_PRICING.lastVerified}; competitor rates verified {COMPETITOR_PRICING.lastVerified}. GPT-5.4 rates apply under 270K context. See the full{" "}
              <a href="/pricing" className="text-primary hover:underline">DeepSeek pricing breakdown</a>.
            </p>
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-4 pb-4">
                <p className="text-sm font-medium text-foreground mb-2">
                  💡 Measured against V4-Pro at ${pro.inputCacheMiss}/${pro.output} per 1M tokens, the actual multiples are:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>vs GPT-5.4:</strong> {multipleVs(gpt54.input, pro.inputCacheMiss)} on input, {multipleVs(gpt54.output, pro.output)} on output</li>
                  <li>• <strong>vs Claude Opus 4.8:</strong> {multipleVs(opus48.input, pro.inputCacheMiss)} on input, {multipleVs(opus48.output, pro.output)} on output</li>
                </ul>
              </CardContent>
            </Card>

          </section>

          {/* Hardware */}
          <section id="hardware" className="mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-3">
              <HardDrive className="h-7 w-7 text-primary" /> Geopolitics and Hardware: The Shift to Huawei
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              One of the most consequential aspects of DeepSeek V4 is its hardware foundation. Due to strict US export restrictions on advanced Nvidia GPUs (like the B300 and H200), DeepSeek has optimized V4 to run heavily on domestic Chinese silicon for inference.
            </p>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              While initial training likely still utilized Nvidia hardware (such as H800s), the model is highly optimized for the <strong>Huawei Ascend 950PR</strong> and <strong>Cambricon MLU</strong> chips.
            </p>
            <Card className="bg-accent/30 border-accent">
              <CardContent className="pt-4 pb-4">
                <p className="text-sm font-medium text-foreground">
                  🔧 Huawei's Ascend 950PR reportedly delivers <strong>2.87x the compute performance</strong> of the Nvidia H20 (the chip legally allowed for export to China). This marks a major milestone in China's push for AI semiconductor independence.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Release Date */}
          <section id="release-date" className="mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Calendar className="h-7 w-7 text-primary" /> DeepSeek V4 Release Timeline: From Launch to V4 Pro
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              DeepSeek V4 launched on 24 April 2026 as a two-model family (Pro and Flash). Below is the timeline from launch through the V4 Pro pricing decision and the retirement of the legacy model IDs.
            </p>

            <div className="space-y-4 mb-6">
              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-4 pb-4">
                  <p className="text-sm"><strong className="text-primary">🚀 April 24, 2026 — V4 Pro &amp; V4 Flash launch:</strong> DeepSeek shipped exactly two V4 variants: V4 Pro (1.6T total / 49B active) and V4 Flash (284B / 13B), both text-only, both with a 1M-token context window and MIT-licensed open weights.</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-4 pb-4">
                  <p className="text-sm"><strong className="text-primary">💸 May 31, 2026 — V4 Pro price cut made permanent:</strong> The 75% discount on V4 Pro became permanent at $0.435/M input (cache miss) and $0.87/M output. <a href="/blog/deepseek-v4-pro-api-price-cut-permanent" className="text-primary hover:underline">Read the full analysis</a>.</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-4 pb-4">
                  <p className="text-sm"><strong className="text-primary">📅 July 24, 2026 — Legacy model IDs retired:</strong> <code>deepseek-chat</code> and <code>deepseek-reasoner</code> were retired at 15:59 UTC. They are no longer routed anywhere — see <a href="/blog/deepseek-chat-reasoner-retired-billing-impact" className="text-primary hover:underline font-medium">what the retirement did to your bill</a>.</p>
                </CardContent>
              </Card>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              MoE routing cuts <em>compute</em> per token, not memory: every expert still has to be resident. V4 Flash's 284B total parameters are roughly <strong>140 GB+</strong> of weights in the shipped mixed precision, and still 70–80 GB heavily quantised — so local inference means multi-GPU server hardware, not a consumer desktop. V4 Pro at 1.6T total params is firmly a data-centre workload. For most teams the hosted API is cheaper than self-hosting once you account for GPU capital, electricity and ops time.
            </p>

          </section>


          {/* FAQ */}
          <section id="faq" className="mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Frequently Asked Questions About DeepSeek V4</h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1">
                <AccordionTrigger>Is DeepSeek V4 better than ChatGPT (GPT-5.4)?</AccordionTrigger>
                <AccordionContent>
                  Based on leaked benchmarks, DeepSeek V4 rivals or slightly beats GPT-5.4 and Claude 4.5 in complex software engineering tasks (SWE-bench) and repository-level coding, especially given its 1M token context. However, independent testing is required to declare an absolute winner once the model officially launches.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-2">
                <AccordionTrigger>Can I run DeepSeek V4 locally?</AccordionTrigger>
                <AccordionContent>
                  Not on consumer GPUs. Mixture-of-Experts routing reduces <em>compute</em> per token, but every expert must still be resident in memory. V4-Flash at 284B total parameters needs roughly <strong>140 GB+</strong> of weights in the shipped mixed precision, before any KV cache — far beyond dual RTX 4090s (48 GB) or a single RTX 5090 (32 GB). Aggressive 4-bit quantisation still lands near 70–80 GB, so realistic local inference means multi-GPU server hardware (for example 2× 80 GB accelerators). V4-Pro at 1.6T total parameters is a data-centre workload.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-3">
                <AccordionTrigger>Why is DeepSeek V4 so cheap?</AccordionTrigger>
                <AccordionContent>
                  The low costs are driven by the highly efficient MoE architecture (activating only a fraction of the model), low training costs (estimated at ~$10M compared to &gt;$100M for Western models), and the implementation of DeepSeek Sparse Attention, which halves the compute needed for long contexts.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-4">
                <AccordionTrigger>Is DeepSeek V4 multimodal?</AccordionTrigger>
                <AccordionContent>
                  No. Both shipped variants — V4-Pro and V4-Flash — are text-only mixture-of-experts language models. The model cards describe no vision, audio or video modality, and DeepSeek's release note mentions none. Pre-launch reports of "native multimodal" training did not ship.
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          </section>

          {/* Conclusion */}
          <section className="mb-12 prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-foreground mb-4">Conclusion</h2>
            <p className="text-muted-foreground leading-relaxed">
              Three months after launch, DeepSeek V4 has done exactly what the leaks predicted: architectural efficiency (mHC plus DeepSeek Sparse Attention with CSA/HCA) beating brute-force scaling on cost-per-quality. With a 1-million-token context window, the V4 Pro permanent price cut, and MIT-licensed open weights, V4 has forced every major API provider to revisit pricing. Both shipped variants are <strong>text-only</strong>. For most teams in mid-2026 the practical choice is V4 Pro for the hardest reasoning and V4 Flash for low-latency, high-throughput agentic use — the two variants DeepSeek actually shipped.
            </p>
          </section>

          {/* Related Links */}
          <div className="border-t border-b border-border py-8 mb-12">
            <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
            <RelatedLinks />
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12 mb-8">
            <a
              href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-primary-foreground bg-primary hover:bg-primary/90 transition-all shadow-lg hover:translate-y-[-1px]"
            >
              Try Deep Seek AI Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <button
              onClick={() => setIsDialogOpen(true)}
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-primary text-base font-medium rounded-full text-primary bg-background hover:bg-accent transition-all shadow-lg hover:translate-y-[-1px]"
            >
              Create AI Agents
              <Bot className="ml-2 h-5 w-5" />
            </button>
          </div>
        </ContentLayout>
      </div>

      <WaitlistDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
    </>
  );
};

export default DeepseekV4;
